'use client'
import { Box, HStack, Separator, Stack, Text } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import CPhoneInput from '../../../PhoneInput'
import { Button } from '@/components/ui/button'
import { CField } from '../../../CField'
import { useSelector } from 'react-redux'
import { IRedux } from '@/app/[locale]/_store/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getPlatform } from '@/app/[locale]/_utils/platform'
import axios from 'axios'
import Image from 'next/image'
import { fonts } from '@/app/[locale]/fonts'

function LoginMobile({
  error,
  loading,
  tgloading,
  open,
  values,
  onChange,
  onLogin,
  onSuccess,
  children,
}: {
  error: boolean
  loading: boolean
  tgloading: boolean
  open?: boolean
  values: { phone: string; name: string }
  onChange: (name: string, value: string) => void
  onLogin: (e: any, onError: () => void) => void
  onSuccess: (data: any) => void
  children: React.ReactNode
}) {
  const queryClient = useQueryClient()
  const [phoneError, setPhoneError] = useState(false)
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false)
  const [loadingState, setLoadingState] = useState(false)
  const intervalRef = useRef<number | null>(null)

  const t = useTranslations()
  const settings = useSelector((state: IRedux) => state.settings)
  const accessToken = useSelector(
    (state: IRedux) => state.auth?.user?.access_token
  )
  const shipperId = settings?.data?.shipper_id

  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        setIsKeyboardOpen(window.visualViewport.height < window.innerHeight)
      }
    }

    window.visualViewport?.addEventListener('resize', handleResize)
    return () =>
      window.visualViewport?.removeEventListener('resize', handleResize)
  }, [])

  const onTelChange = (val: string) => {
    if (phoneError) setPhoneError(false)

    onChange('phone', val)
  }

  const getTelegramBotLink = async (id: string) => {
    const platformId = getPlatform()
    const { data } = await axios.get('/v1/customers/telegram-auth-bot', {
      baseURL: process.env.NEXT_PUBLIC_BASE_URL,
      headers: {
        Shipper: id,
        Platform: platformId,
        ...(accessToken ? { Authorization: accessToken } : {}),
      },
    })

    return data
  }

  const { data, refetch } = useQuery({
    queryKey: ['telegramBotLink', shipperId, accessToken],
    queryFn: () => getTelegramBotLink(shipperId as string),
    enabled: Boolean(shipperId),
  })

  const sendingSessionId = async (sessionId: string) => {
    if (!sessionId || !shipperId) return

    try {
      const res = await axios.post(
        '/v1/customers/telegram-auth-status',
        { session_id: sessionId },
        {
          baseURL: process.env.NEXT_PUBLIC_BASE_URL,
          headers: {
            Shipper: shipperId,
          },
        }
      )

      const userData = res?.data?.data ?? res?.data
      if (userData) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
        queryClient.removeQueries({
          queryKey: ['telegramBotLink', shipperId, accessToken],
        })
        refetch()
        onSuccess(userData)
        setLoadingState(false)
      }
    } catch(error){
      console.log(error)
    }
  }

  useEffect(() => {
    if (!open) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      setLoadingState(false)
    }
  }, [open])

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  return (
    <Stack
      as='form'
      onSubmit={(e) => onLogin(e, () => setPhoneError(true))}
      style={{ flex: 1 }}
      gap={4}
    >
      <Text color={'text'} fontSize='2xl' fontWeight={700} mb={2}>
        {t('login')}
      </Text>
      <CField
        invalid={phoneError || error}
        errorText={
          phoneError ? t('incorrect_phone_number') : t('error_occured')
        }
      >
        <CPhoneInput
          label={t('phone_number')}
          value={values?.phone}
          onChange={(e) => typeof e === 'string' && onTelChange(e)}
        />
      </CField>
      <Text color={'text'} fontSize='sm' textAlign={'start'}>
        {t('permission_privacy_policy')}{' '}
        <Text as='span' color='primary'>
          {t('terms_of_use')}
        </Text>
      </Text>
      <Button
        type='submit'
        fontSize='md'
        height='56px'
        bg='primary'
        color='primary.text'
        _hover={{
          bg: 'primary.hover',
        }}
        loading={loading}
      >
        {settings?.data?.data?.is_otp ? t('send_code') : t('continue')}
      </Button>

      {!settings?.data?.data?.hide_google_auth ? (
        <Stack flex={1} justify='flex-end'>
          {!isKeyboardOpen && (
            <HStack my={1}>
              <Separator flex='1' />
              <Text color={'gray.700'} flexShrink='0'>
                {t('or')}
              </Text>
              <Separator flex='1' />
            </HStack>
          )}

          <HStack ml={'-4px'} justifyContent={'center'} gap={2} wrap='wrap' alignItems='center'>
            <Box width='230px'>{children}</Box>

            {settings?.data?.data?.telegram_auth ? (
              <>
                <Button
                  size='xl'
                  fontSize='md'
                  height='41px'
                  width='220px'
                  bg='#0088CC'
                  color='primary.text'
                  loading={loadingState}
                  disabled={loadingState}
                  borderRadius={'20px'}
                  _hover={{
                    bg: '#0088CC',
                  }}
                  padding={0}
                  onClick={() => {
                    setLoadingState(true)
                    if (data?.bot_link) {
                      window.open(data.bot_link, '_blank', 'noopener,noreferrer')
                      if (intervalRef.current) {
                        clearInterval(intervalRef.current)
                      }
                      intervalRef.current = window.setInterval(() => {
                        sendingSessionId(data.session_id)
                      }, 3000)
                    }
                  }}
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                  <HStack gap={2}>
                    <Image
                      src='/telegram-svgrepo-com.svg'
                      alt='Telegram'
                      width={20}
                      height={20}
                    />
                    <Text style={{ fontSize: '13px' }}>{t('auth_via_tg')}</Text>
                  </HStack>
                </Button>
              </>
            ) : null}
          </HStack>
        </Stack>
      ) : null}

      {settings?.data?.data?.telegram_auth && settings?.data?.data?.hide_google_auth ? (
        <>
          <HStack my={1}>
            <Separator flex='1' />
            <Text color={'gray.700'} flexShrink='0'>
              {t('or')}
            </Text>
            <Separator flex='1' />
          </HStack>
          <HStack w='full' justifyContent={'center'} gap={2} alignItems='center'>
            <Button
              size='xl'
              fontSize='md'
              height='41px'
              width='220px'
              bg='#0088CC'
              color='primary.text'
              loading={loadingState}
              disabled={loadingState}
              borderRadius={'20px'}
              _hover={{
                bg: '#0088CC',
              }}
              onClick={() => {
                setLoadingState(true)
                if (data?.bot_link) {
                  window.open(data.bot_link, '_blank', 'noopener,noreferrer')
                  if (intervalRef.current) {
                    clearInterval(intervalRef.current)
                  }
                  intervalRef.current = window.setInterval(() => {
                    sendingSessionId(data.session_id)
                  }, 3000)
                }
              }}
              style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              <HStack gap={2}>
                <Image
                  src='/telegram-svgrepo-com.svg'
                  alt='Telegram'
                  width={20}
                  height={20}
                />
                <Text style={{ fontSize: '13px' }}>{t('auth_via_tg')}</Text>
              </HStack>
            </Button>
          </HStack>
        </>
      ) : null}
    </Stack>
  )
}

export default LoginMobile
